import { useEffect, useState } from 'react';
import { useClientStore } from '@core/client/useClientStore';
import type { Client } from '@core/generated/gql-types';
import type { ClientDetailView } from '@/modules/clients/views/clientDetail.view';

type UpdateClientResult = {
  success: boolean;
  client?: Client;
  error?: string;
};

type UseClientDetailData = {
  client: Client | null;
  isLoading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  refetchClient: () => Promise<void>;
  updateClientDetails: (
    updatedClient: Partial<Client>,
  ) => Promise<UpdateClientResult>;
};

export const useClientDetailData = (
  view: ClientDetailView,
  clientId: string | undefined,
): UseClientDetailData => {
  const { fetchClientById, clients, updateClient } = useClientStore();
  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId || !view.query.client.fetch) return;

    setClient(
      clients.find((storeClient) => storeClient.id === clientId) ?? null,
    );
    if (!client) {
      void fetchClientById(clientId);
    }
  }, [clientId, fetchClientById, clients, client, view.query.client.fetch]);

  const refetchClient = async () => {
    if (!clientId || !view.query.client.fetch) return;
    await fetchClientById(clientId);
  };

  const updateClientDetails = async (
    updatedClient: Partial<Client>,
  ): Promise<UpdateClientResult> => {
    if (!clientId) {
      return { success: false, error: 'Client ID is required' };
    }

    const result = await updateClient(clientId, updatedClient);
    if (result.success && result.client) {
      setClient(result.client);
      return { success: true, client: result.client };
    }

    const updateError = result.error ?? 'Failed to update client';
    setError(updateError);
    return { success: false, error: updateError };
  };

  return {
    client,
    isLoading,
    error,
    setError,
    refetchClient,
    updateClientDetails,
  };
};
